<template>
  <BittsContainer class="api-resource-card">
    <div class="api-resource-card__header">
      <div class="api-resource-card__identity">
        <span v-if="status" class="api-resource-card__dot" :class="dotClass" />
        <h3 class="api-resource-card__name">
          {{ name }}
        </h3>
        <BittsBadge v-if="statusText" :status="status" :text="statusText" />
      </div>
      <div v-if="$slots.actions" class="api-resource-card__actions">
        <slot name="actions" />
      </div>
    </div>
    <div
      v-if="$slots.default"
      class="api-resource-card__body"
      :class="{ 'api-resource-card--indented': status }"
    >
      <slot />
    </div>
    <div
      v-if="$slots.footer"
      class="api-resource-card__footer"
      :class="{ 'api-resource-card--indented': status }"
    >
      <slot name="footer" />
    </div>
    <div v-if="$slots.expansion" class="api-resource-card__expansion">
      <slot name="expansion" />
    </div>
  </BittsContainer>
</template>

<script setup lang="ts">
import { BittsBadge, BittsContainer } from '@crossbeam/bitts';

import { computed } from 'vue';

import type { ApiResourceStatusType } from '../types';

const {
  name,
  status,
  statusText = '',
} = defineProps<{
  name: string;
  status?: ApiResourceStatusType;
  statusText?: string;
}>();

const dotClass = computed(
  () =>
    ({
      success: 'bg-success-accent',
      warning: 'bg-warning-accent',
      danger: 'bg-danger-accent',
      neutral: 'bg-neutral-accent',
    })[status ?? 'success'],
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-resource-card {
  @apply flex flex-col gap-16;
}

.api-resource-card__header {
  @apply flex items-center justify-between gap-16;
}

.api-resource-card__identity {
  @apply flex items-center gap-8 min-w-0;
}

.api-resource-card__dot {
  @apply size-8 rounded-full shrink-0;
}

.api-resource-card__name {
  @apply text-m font-bold text-neutral-text-strong m-0 truncate;
}

.api-resource-card--indented {
  @apply pl-16;
}

.api-resource-card__actions {
  @apply flex items-center gap-8 shrink-0;
}

.api-resource-card__footer {
  @apply flex items-center justify-between gap-16 flex-wrap;
}

.api-resource-card__expansion {
  @apply bg-neutral-background-weak rounded-2xl p-16 flex flex-col gap-16;
}
</style>
